// EMR launch link: a stable URL a clinic adds to its EMR toolbar.
//
//   https://app.rosc.ai/launch[?action=new-visit|latest-visit][&template=<id>][&source=emr]
//
// - action    new-visit (default): Visits page with the New visit sheet open,
//             ready to record. latest-visit: the most recent visit.
// - template  Optional. soap | specialist | dictation, or the id of one of the
//             user's own templates (from Templates). Unknown values are ignored.
// - source    Optional, analytics only (letters, digits, - and _). `ref` is
//             accepted as an alias.
//
// PRIVACY: the link is the same for every patient. It never carries patient
// identifiers (name, health card, MRN, date of birth, chart number…) and
// any parameter not listed above is dropped before routing, so nothing an
// EMR appends to the URL is kept, logged or forwarded.

import { publicIdSchema } from "@rosc/contracts";
import { DefaultRecordingTemplates } from "@/common/enums/enums";

export const LAUNCH_PATH = "/launch";
export const LAUNCH_ACTIONS = ["new-visit", "latest-visit"] as const;
export type LaunchAction = (typeof LAUNCH_ACTIONS)[number];

export const LAUNCH_TEMPLATE_ALIASES = {
  soap: DefaultRecordingTemplates.SOAP,
  specialist: DefaultRecordingTemplates.SPECIALIST,
  dictation: DefaultRecordingTemplates.VOICE_TO_TEXT,
} as const;
export type LaunchTemplateAlias = keyof typeof LAUNCH_TEMPLATE_ALIASES;

export interface LaunchParams {
  action: LaunchAction;
  /** A template alias or a template public id (uuid). */
  template?: string;
  source?: string;
}

const SOURCE_PATTERN = /^[A-Za-z0-9_-]{1,32}$/;

function pick(value: unknown): string | undefined {
  const first = Array.isArray(value) ? value[0] : value;
  if (typeof first === "string") return first.trim();
  if (typeof first === "number") return String(first);
  return undefined;
}

export function normalizeLaunchTemplate(value: unknown): string | undefined {
  const raw = pick(value);
  if (!raw) return undefined;
  const alias = raw.toLowerCase();
  if (alias in LAUNCH_TEMPLATE_ALIASES) return alias;
  const id = publicIdSchema.safeParse(raw);
  return id.success ? id.data : undefined;
}

/**
 * Keeps only the documented, non-identifying parameters. Everything else
 * (including anything an EMR appends) is dropped.
 */
export function parseLaunchParams(
  search: Record<string, unknown>,
): LaunchParams {
  const action = pick(search["action"])?.toLowerCase();
  const source = pick(search["source"]) ?? pick(search["ref"]);
  const template = normalizeLaunchTemplate(search["template"]);
  return {
    action: LAUNCH_ACTIONS.includes(action as LaunchAction)
      ? (action as LaunchAction)
      : "new-visit",
    ...(template ? { template } : {}),
    ...(source && SOURCE_PATTERN.test(source) ? { source } : {}),
  };
}

export function launchSearch(params: LaunchParams): Record<string, string> {
  return {
    ...(params.action !== "new-visit" ? { action: params.action } : {}),
    ...(params.template ? { template: params.template } : {}),
    ...(params.source ? { source: params.source } : {}),
  };
}

/** The link shown in Settings. */
export function buildLaunchUrl(
  origin: string,
  params: Partial<LaunchParams> = {},
): string {
  const url = new URL(LAUNCH_PATH, origin);
  const search = launchSearch({
    action: params.action ?? "new-visit",
    template: normalizeLaunchTemplate(params.template),
    source: params.source,
  });
  for (const [key, value] of Object.entries(search))
    url.searchParams.set(key, value);
  return url.toString();
}

/** Resolves a launch template value to the New visit form's template value. */
export function resolveLaunchTemplate(
  template: string | undefined,
): string | undefined {
  if (!template) return undefined;
  if (template in LAUNCH_TEMPLATE_ALIASES)
    return LAUNCH_TEMPLATE_ALIASES[template as LaunchTemplateAlias];
  return template;
}

// --- Continue after sign-in -------------------------------------------------
// The launch is remembered in this tab (sessionStorage) for a few minutes so
// it survives password, OTP, passkey and Google sign-in redirects. Only the
// sanitized params above are stored; never patient data.

const PENDING_KEY = "rosc:emr-launch:pending";
const PENDING_TTL_MS = 15 * 60 * 1000;

export function rememberPendingLaunch(
  params: LaunchParams,
  now: number = Date.now(),
): void {
  try {
    sessionStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ ...launchSearch(params), at: now }),
    );
  } catch {
    // Storage unavailable: the user lands on Visits after sign-in instead.
  }
}

export function clearPendingLaunch(): void {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // Nothing stored.
  }
}

/**
 * The /launch URL to continue to after sign-in, or null. It is cleared when
 * /launch runs for a signed-in user (or when it has expired), so repeated
 * renders of the sign-in page all agree on the destination.
 */
export function pendingLaunchUrl(now: number = Date.now()): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(PENDING_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== "object") throw Error("invalid");
    const { at, ...search } = value as Record<string, unknown>;
    if (typeof at !== "number" || now - at > PENDING_TTL_MS || now < at)
      throw Error("expired");
    const query = new URLSearchParams(
      launchSearch(parseLaunchParams(search)),
    ).toString();
    return query ? `${LAUNCH_PATH}?${query}` : LAUNCH_PATH;
  } catch {
    clearPendingLaunch();
    return null;
  }
}
